/**
 * Overview Section
 *
 * Displays key analytics metrics with stat cards and top lists.
 */

import { useTranslation } from 'react-i18next'
import {
    MessageSquare,
    FileText,
    Layers,
    Globe,
    ArrowUpRight,
    ArrowDownRight,
} from 'lucide-react'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getAnalyticsOverview } from '../../services/analytics'
import { OverviewComparison } from '../../types/analytics'

interface OverviewSectionProps {
    from: string
    to: string
}

/** Get trend icon and color */
const getTrendDisplay = (trend?: number) => {
    if (!trend || trend === 0) return null
    if (trend > 0) {
        return {
            icon: ArrowUpRight,
            color: 'text-green-600',
            text: `+${trend.toFixed(1)}%`,
        }
    }
    return {
        icon: ArrowDownRight,
        color: 'text-red-600',
        text: `${trend.toFixed(1)}%`,
    }
}

const OverviewSection: React.FC<OverviewSectionProps> = ({ from, to }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler<OverviewComparison>({
        queryKey: [QUERY_KEYS.LIVECHAT_ANALYTICS_OVERVIEW, from, to],
        queryFn: () => getAnalyticsOverview({ from, to, compare: true }),
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                {t('No analytics data available')}
            </div>
        )
    }

    const { current, trends } = data

    const statCards = [
        {
            title: t('Total Conversations'),
            value: current.totalRooms.toLocaleString(),
            trend: trends?.totalRooms,
            icon: MessageSquare,
            iconColor: 'text-blue-600',
        },
        {
            title: t('Total Page Visits'),
            value: current.totalPageVisits.toLocaleString(),
            trend: trends?.totalPageVisits,
            icon: FileText,
            iconColor: 'text-cyan-600',
        },
        {
            title: t('Avg Pages/Session'),
            value: current.avgPagesPerSession.toFixed(1),
            trend: trends?.avgPagesPerSession,
            icon: Layers,
            iconColor: 'text-purple-600',
        },
        {
            title: t('Cross-site Visitors'),
            value: current.crossSiteVisitors.toLocaleString(),
            description: t('Visitors across multiple domains'),
            trend: trends?.crossSiteVisitors,
            icon: Globe,
            iconColor: 'text-amber-600',
        },
    ]

    return (
        <div>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2">
                {statCards.map((card) => {
                    const Icon = card.icon
                    const trendDisplay = getTrendDisplay(card.trend)

                    return (
                        <Card
                            key={card.title}
                            className="lg:col-span-3 md:col-span-6 col-span-12"
                        >
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">
                                    {card.title}
                                </CardTitle>
                                <Icon className={`h-5 w-5 ${card.iconColor}`} />
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-bold">
                                        {card.value}
                                    </span>
                                    {trendDisplay && (
                                        <span
                                            className={`text-xs font-medium ${trendDisplay.color}`}
                                        >
                                            {trendDisplay.text}
                                        </span>
                                    )}
                                </div>
                                {card.description && (
                                    <CardDescription className="mt-1 text-xs">
                                        {card.description}
                                    </CardDescription>
                                )}
                            </CardContent>
                        </Card>
                    )
                })}
            </div>

            {/* Top Sources & Landing Pages */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Top Sources */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('Top Sources')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {current.topSources.length > 0 ? (
                                current.topSources
                                    .slice(0, 5)
                                    .map((source, index) => (
                                        <div
                                            key={source.origin}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-muted-foreground w-4">
                                                    {index + 1}
                                                </span>
                                                <span className="text-sm truncate max-w-[200px]">
                                                    {source.origin ||
                                                        t('Direct')}
                                                </span>
                                            </div>
                                            <span className="text-sm font-medium">
                                                {source.count.toLocaleString()}
                                            </span>
                                        </div>
                                    ))
                            ) : (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                    {t('No data available')}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Top Landing Pages */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('Top Landing Pages')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {current.topLandingPages.length > 0 ? (
                                current.topLandingPages
                                    .slice(0, 5)
                                    .map((page, index) => (
                                        <div
                                            key={page.url}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="text-sm text-muted-foreground w-4">
                                                    {index + 1}
                                                </span>
                                                {page.fullUrl ? (
                                                    <a
                                                        href={page.fullUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:underline truncate cursor-pointer"
                                                        title={page.fullUrl}
                                                    >
                                                        {page.title || page.url}
                                                    </a>
                                                ) : (
                                                    <span
                                                        className="text-sm truncate"
                                                        title={page.url}
                                                    >
                                                        {page.title || page.url}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-sm font-medium ml-2">
                                                {page.count.toLocaleString()}
                                            </span>
                                        </div>
                                    ))
                            ) : (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                    {t('No data available')}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default OverviewSection
